import { getServiceRows, populateExpandoServiceData, ProductTile, transformServicesList } from '@ahmdigital/ui';
import { includes, isEmpty, map, omit } from 'lodash/fp';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback } from 'react';

import { IS_CHOOSE_YOUR_COVER } from '../../../../../product/util/use-is-from-choose-your-cover';
import { IS_CHOOSE_YOUR_EXTRAS_COVER } from '../../../../../product/util/use-is-from-choose-your-extras-cover';
import { JourneyProductType } from '../../../../types';
import { MovingColumn } from '../../styles';
import { ProductType, ProductTypeType } from '../../../../../../types/product';
import { RootState } from '../../../../../../reducers';
import buildYourCoverActions, { BuildYourCoverProductType } from '../../../../../../actions/build-your-cover';
import LoadingSpinner from '../../../../../../components/loading-spinner';
import prepareProductsForProductTiles from '../../../../../../utils/prepare-products-for-product-tiles';
import selectCustomer from '../../../../../../selectors/select-customer';
import selectPrices from '../../../../../../selectors/select-prices';
import truncateFrequency from '../../../../../choosable/pages/picker/utils/truncate-frequency';

type CarouselTilesProps = {
  products: ProductType[];
  hasBestSellerWithFewerInclusions: boolean;
  isExpanded: boolean;
  isStuck: boolean;
  handleToggle: () => void;
  journeyProductType: JourneyProductType;
};

const CarouselTiles = ({
  products,
  hasBestSellerWithFewerInclusions,
  isExpanded,
  isStuck,
  handleToggle,
  journeyProductType,
}: CarouselTilesProps) => {
  const dispatch = useDispatch();
  const customer = useSelector(selectCustomer);
  const prices = useSelector(selectPrices);
  const { selectedProducts } = useSelector((state: RootState) => state.buildYourCover);
  const useFutureEffectiveDate = useSelector((state: RootState) => state.price.useFutureEffectiveDate);

  const isBuildYourCoverProductType = (productType: ProductType['type']): productType is BuildYourCoverProductType =>
    productType === PRODUCT_TYPE.EXTRAS || productType === PRODUCT_TYPE.HOSPITAL;

  const productTileQueryString =
    journeyProductType === PRODUCT_TYPE.EXTRAS ? IS_CHOOSE_YOUR_EXTRAS_COVER : IS_CHOOSE_YOUR_COVER;
  const handleProductSelect = useCallback(
    (productType: ProductType['type'], productId: ProductType['id']) => {
      if (isBuildYourCoverProductType(productType)) {
        dispatch(buildYourCoverActions.setOrUnsetSelectedProduct(productType, productId));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  if (isEmpty(products)) {
    return <LoadingSpinner />;
  }

  const expandoProducts = populateExpandoServiceData(products);
  const mappedProducts = expandoProducts.map((product: ProductType) => ({
    ...omit([PRODUCT_TYPE.EXTRAS, PRODUCT_TYPE.HOSPITAL], product),
    [`${product.type}Services`]: transformServicesList(product, product.type),
  }));

  const tilesProductType = products[0]?.type as ProductTypeType;
  const allServices = getServiceRows(mappedProducts, tilesProductType);

  const treatedProducts = prepareProductsForProductTiles(products, customer, prices, useFutureEffectiveDate);

  const selectedProductId = selectedProducts[tilesProductType];
  const productIds = map('id', products);
  if (selectedProductId && !includes(selectedProductId, productIds)) {
    handleProductSelect(tilesProductType, selectedProductId);
  }

  return (
    <>
      {treatedProducts.map((product) => (
        <MovingColumn key={product?.id} variant="extraThinGutter">
          <ProductTile
            {...product}
            url={`${product?.url}?${productTileQueryString}=true`}
            onToggle={handleToggle}
            handleSelect={handleProductSelect}
            isBestSeller={product?.isBestSeller}
            isExpandable
            isExpanded={isExpanded}
            isBestSellerWithFewerInclusions={hasBestSellerWithFewerInclusions}
            isStuck={isStuck}
            ctaIcon="plus-circle"
            ctaText="Add cover"
            scale={product?.scale}
            selectedProductId={selectedProductId}
            services={allServices}
            frequency={truncateFrequency(customer.frequency)}
            price={product?.price}
          />
        </MovingColumn>
      ))}
    </>
  );
};

export default CarouselTiles;
